import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import '../styles/BestellingBewerkenPage.css';
import '../styles/NieuweBestellingPage.css';
import AndersProductenPage from './AndersProductenPage';
import FeedbackModal from '../components/FeedbackModal';
import PageLoader from '../components/PageLoader';
import ProductZoekenLijst from '../components/ProductZoekenLijst';
import { useProductZoeken } from '../hooks/useProductZoeken';

function BestellingBewerkenPage({ user, bestellingId, onBack, onLogout }) {
  const [bestelling, setBestelling] = useState(null);
  const [regels, setRegels] = useState([]);
  const [vrijeRegels, setVrijeRegels] = useState([]);
  const [hoeveelheden, setHoeveelheden] = useState({});
  const [vrijeHoeveelheden, setVrijeHoeveelheden] = useState({});
  const [productgroepen, setProductgroepen] = useState([]);
  const [groepenProducten, setGroepenProducten] = useState({});
  const [activeGroepId, setActiveGroepId] = useState(null);
  const [newQuantities, setNewQuantities] = useState({});
  const [newVrijeRegels, setNewVrijeRegels] = useState([]);
  const [showAndersPage, setShowAndersPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [notitie, setNotitie] = useState('');

  const productenPanelRef = useRef(null);
  const { query: zoekQuery, setQuery: setZoekQuery, results: zoekResultaten, loading: zoekLaden, isSearching } = useProductZoeken();

  const isAndersGroep = (groep) => groep.naam.toLowerCase() === 'anders';
  const andersGroep = productgroepen.find(isAndersGroep);

  const markDirty = () => setIsDirty(true);

  const handleLeave = () => {
    if (isDirty && !successMessage) {
      setConfirmLeave(true);
      return;
    }
    onBack();
  };

  useEffect(() => {
    let cancelled = false;

    const fetchPageData = async () => {
      try {
        const [detailsResponse, groepenResponse] = await Promise.all([
          api.get(`/index.php?action=bestellingdetails&bstlid=${bestellingId}`),
          api.get('/index.php?action=productgroepen')
        ]);
        if (cancelled) return;

        setBestelling(detailsResponse.data.bestelling);
        setRegels(detailsResponse.data.regels);
        setVrijeRegels(detailsResponse.data.vrije_regels || []);
        setProductgroepen(groepenResponse.data);

        const initial = {};
        detailsResponse.data.regels.forEach((regel) => {
          initial[regel.ID] = regel.hoeveelheid;
        });
        setHoeveelheden(initial);

        const initialVrij = {};
        (detailsResponse.data.vrije_regels || []).forEach((regel) => {
          initialVrij[regel.ID] = regel.hoeveelheid;
        });
        setVrijeHoeveelheden(initialVrij);
        setNotitie(detailsResponse.data.bestelling.notitie || '');
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError('Fout bij laden van bestelling');
        setLoading(false);
      }
    };

    fetchPageData();
    return () => {
      cancelled = true;
    };
  }, [bestellingId]);

  const loadProducten = async (groepId) => {
    try {
      const response = await api.get(`/index.php?action=producten&groep=${groepId}`);
      setGroepenProducten((prev) => ({
        ...prev,
        [groepId]: response.data
      }));
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const getActiveProductIds = () =>
    new Set(
      regels
        .filter((regel) => (hoeveelheden[regel.ID] || 0) > 0)
        .map((regel) => String(regel.prodid))
    );

  const handleGroepClick = (groep) => {
    if (isAndersGroep(groep)) {
      setShowAndersPage(true);
      return;
    }

    const groepId = groep.ID;

    if (activeGroepId === groepId) {
      setActiveGroepId(null);
      return;
    }

    setActiveGroepId(groepId);

    if (!groepenProducten[groepId]) {
      loadProducten(groepId);
    }

    requestAnimationFrame(() => {
      productenPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handleQuantityChange = (regelId, newValue) => {
    markDirty();
    const quantity = Math.max(0, parseInt(newValue, 10) || 0);
    setHoeveelheden({
      ...hoeveelheden,
      [regelId]: quantity
    });
  };

  const handleDeleteRegel = (regelId) => {
    markDirty();
    setHoeveelheden({
      ...hoeveelheden,
      [regelId]: 0
    });
  };

  const handleVrijeQuantityChange = (regelId, newValue) => {
    markDirty();
    const quantity = Math.max(0, parseInt(newValue, 10) || 0);
    setVrijeHoeveelheden({
      ...vrijeHoeveelheden,
      [regelId]: quantity
    });
  };

  const handleDeleteVrijeRegel = (regelId) => {
    markDirty();
    setVrijeHoeveelheden({
      ...vrijeHoeveelheden,
      [regelId]: 0
    });
  };

  const handleNewQuantityChange = (productId, value) => {
    markDirty();
    setNewQuantities({
      ...newQuantities,
      [productId]: value === '' ? '' : parseInt(value, 10) || 0
    });
  };

  const handleSave = async () => {
    const nieuweRegels = Object.entries(newQuantities)
      .filter(([_, qty]) => parseInt(qty, 10) > 0)
      .reduce((acc, [prodid, qty]) => {
        acc[prodid] = qty;
        return acc;
      }, {});

    const hasExistingItems =
      regels.some((regel) => (hoeveelheden[regel.ID] || 0) > 0) ||
      vrijeRegels.some((regel) => (vrijeHoeveelheden[regel.ID] || 0) > 0);

    const hasNewItems =
      Object.keys(nieuweRegels).length > 0 || newVrijeRegels.length > 0;

    if (!hasExistingItems && !hasNewItems) {
      setError('Bestelling moet minimaal één product bevatten');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const updates = {};
      regels.forEach((regel) => {
        updates[regel.ID] = hoeveelheden[regel.ID] || 0;
      });

      const vrije_updates = {};
      vrijeRegels.forEach((regel) => {
        vrije_updates[regel.ID] = vrijeHoeveelheden[regel.ID] || 0;
      });

      await api.post('/index.php?action=updatebestelling', {
        bstlid: bestellingId,
        updates,
        vrije_updates,
        nieuwe_regels: nieuweRegels,
        vrije_nieuwe_regels: newVrijeRegels,
        notitie: notitie.trim() || null
      });

      setIsDirty(false);
      setSuccessMessage('Je wijzigingen zijn opgeslagen.');
      setSaving(false);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Fout bij opslaan van bestelling';
      setError(errorMsg);
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  if (!bestelling) {
    return (
      <div className="bestelling-bewerken-container">
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <p>Bestelling niet gevonden</p>
          <button onClick={onBack} className="primary-button">Terug</button>
        </div>
      </div>
    );
  }

  if (showAndersPage) {
    return (
      <AndersProductenPage
        vrijeRegels={newVrijeRegels}
        onSave={(regels) => {
          markDirty();
          setNewVrijeRegels(regels);
        }}
        onBack={() => setShowAndersPage(false)}
        onLogout={onLogout}
        saveLabel="Toevoegen aan bestelling"
      />
    );
  }

  const activeProductIds = getActiveProductIds();

  return (
    <div className="bestelling-bewerken-container">
      {successMessage && (
        <FeedbackModal
          title="Opgeslagen"
          message={successMessage}
          onConfirm={() => {
            setSuccessMessage(null);
            onBack();
          }}
          confirmLabel="Ok"
        />
      )}

      {confirmLeave && (
        <FeedbackModal
          title="Wijzigingen annuleren?"
          message="Je hebt wijzigingen die nog niet zijn opgeslagen. Weet je zeker dat je wilt teruggaan?"
          onConfirm={() => {
            setConfirmLeave(false);
            onBack();
          }}
          onCancel={() => setConfirmLeave(false)}
          confirmLabel="Ja, terug"
          cancelLabel="Nee, blijven"
        />
      )}

      <header className="bestelling-header">
        <button type="button" onClick={handleLeave} className="back-button">← Terug</button>
        <h1>Bestelling bewerken</h1>
        <button onClick={onLogout} className="logout-button">Uitloggen</button>
      </header>

      <main className="bestelling-main">
        <div className="bewerken-content">
          {error && <div className="error-message">{error}</div>}

          <div className="bestelling-info">
            <div className="info-card">
              <h3>Bestellingsinformatie</h3>
              <div className="info-row">
                <label>Bestelnummer:</label>
                <span>#{bestelling.ID}</span>
              </div>
              <div className="info-row">
                <label>Besteldatum:</label>
                <span>{bestelling.besteldatum}</span>
              </div>
              <div className="info-row">
                <label>Leverdatum:</label>
                <span>{bestelling.leverdatum}</span>
              </div>
              <div className="info-row">
                <label>Status:</label>
                <span className={`status status-${bestelling.status === 0 ? 'open' : 'gesloten'}`}>
                  {bestelling.status === 0 ? 'Open' : 'Gesloten'}
                </span>
              </div>
            </div>
          </div>

          <div className="regels-bewerken-card">
            <h3>Notitie</h3>
            <p className="info-text">
              Voeg een optionele opmerking toe aan je bestelling, bijvoorbeeld allergieën of speciale wensen.
            </p>
            <textarea
              id="bestelling-notitie-bewerken"
              className="bestelling-notitie-input"
              value={notitie}
              onChange={(e) => {
                markDirty();
                setNotitie(e.target.value);
              }}
              placeholder="Bijv. allergieën, speciale wensen of opmerkingen voor de kok..."
              rows={3}
              maxLength={500}
              disabled={bestelling.status !== 0}
            />
            <small className="notitie-hint">{notitie.length}/500 tekens</small>
          </div>

          <div className="regels-bewerken-card">
            <h3>Huidige bestelling</h3>
            <p className="info-text">
              Pas hoeveelheden aan of verwijder producten uit je bestelling. Bestaande vrije producten (Anders) kun je alleen in hoeveelheid wijzigen; nieuwe vrije producten voeg je hieronder toe.
            </p>

            {regels.length === 0 && vrijeRegels.length === 0 ? (
              <p className="empty-order-text">Nog geen producten in deze bestelling.</p>
            ) : (
              <div className="regels-bewerken-list">
                {regels.map((regel) => (
                  <div key={regel.ID} className="regel-item">
                    <div className="regel-info">
                      <h4>{regel.product}</h4>
                      <p className="regel-unit">Eenheid: {regel.eenheid || '-'}</p>
                    </div>

                    <div className="regel-quantity">
                      <label htmlFor={`qty-${regel.ID}`}>Hoeveelheid:</label>
                      <input
                        id={`qty-${regel.ID}`}
                        type="number"
                        min="0"
                        value={hoeveelheden[regel.ID] || 0}
                        onChange={(e) => handleQuantityChange(regel.ID, e.target.value)}
                        className="quantity-input"
                      />
                    </div>

                    <button
                      className="delete-button"
                      onClick={() => handleDeleteRegel(regel.ID)}
                      title="Verwijder dit product uit de bestelling"
                    >
                      ✕ Verwijderen
                    </button>
                  </div>
                ))}
                {vrijeRegels.map((regel) => (
                  <div key={`vrij-${regel.ID}`} className="regel-item regel-item-anders">
                    <div className="regel-info">
                      <h4>{regel.omschrijving}</h4>
                      <p className="regel-unit">Vrij product (Anders)</p>
                    </div>

                    <div className="regel-quantity">
                      <label htmlFor={`vrij-qty-${regel.ID}`}>Hoeveelheid:</label>
                      <input
                        id={`vrij-qty-${regel.ID}`}
                        type="number"
                        min="0"
                        value={vrijeHoeveelheden[regel.ID] || 0}
                        onChange={(e) => handleVrijeQuantityChange(regel.ID, e.target.value)}
                        className="quantity-input"
                      />
                    </div>

                    <button
                      className="delete-button"
                      onClick={() => handleDeleteVrijeRegel(regel.ID)}
                      title="Verwijder dit product uit de bestelling"
                    >
                      ✕ Verwijderen
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="regels-bewerken-card producten-toevoegen-card">
            <h3>Producten toevoegen</h3>
            <p className="info-text">
              Kies een productgroep om extra producten toe te voegen, net als bij een nieuwe bestelling.
            </p>

            <div className="form-section productgroepen-section">
              <div className="product-zoeken-bar">
                <label htmlFor="product-zoeken-bewerken">Zoek product</label>
                <input
                  id="product-zoeken-bewerken"
                  type="search"
                  className="product-zoeken-input"
                  placeholder="Zoek in alle producten..."
                  value={zoekQuery}
                  onChange={(e) => setZoekQuery(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="bestel-product-layout">
                <aside className="productgroepen-panel">
                  <p className="panel-hint">Kies een productgroep</p>
                  <div className="productgroepen-list">
                    {productgroepen.length === 0 ? (
                      <p className="no-groups">Geen productgroepen beschikbaar</p>
                    ) : (
                      productgroepen
                        .filter((groep) => !isAndersGroep(groep))
                        .map((groep) => (
                          <button
                            key={groep.ID}
                            type="button"
                            className={`productgroep-item ${activeGroepId === groep.ID ? 'active' : ''}`}
                            onClick={() => handleGroepClick(groep)}
                            title={groep.beschrijving}
                          >
                            {groep.naam}
                          </button>
                        ))
                    )}
                    {andersGroep && (
                      <button
                        type="button"
                        className={`productgroep-item ${newVrijeRegels.length > 0 ? 'active' : ''}`}
                        onClick={() => handleGroepClick(andersGroep)}
                        title="Producten die niet in de standaard lijst staan"
                      >
                        {andersGroep.naam}
                        {newVrijeRegels.length > 0 && (
                          <span className="anders-badge"> ({newVrijeRegels.length})</span>
                        )}
                      </button>
                    )}
                  </div>
                </aside>

                <div className="producten-panel" ref={productenPanelRef}>
                  {isSearching ? (
                    <div className="producten-panel-content">
                      <h3 className="producten-panel-title">Zoekresultaten</h3>
                      <ProductZoekenLijst
                        results={zoekResultaten}
                        loading={zoekLaden}
                        quantities={newQuantities}
                        onQuantityChange={handleNewQuantityChange}
                        excludeProductIds={activeProductIds}
                        emptyMessage="Geen producten gevonden of ze staan al in je bestelling."
                      />
                    </div>
                  ) : activeGroepId ? (
                    (() => {
                      const groep = productgroepen.find((g) => g.ID === activeGroepId);
                      const producten = (groepenProducten[activeGroepId] || []).filter(
                        (product) => !activeProductIds.has(String(product.ID))
                      );

                      return (
                        <div className="producten-panel-content">
                          <h3 className="producten-panel-title">
                            Producten in &quot;{groep?.naam}&quot;
                          </h3>
                          {producten.length > 0 ? (
                            <div className="producten-list">
                              {producten.map((product) => (
                                <div key={product.ID} className="product-item">
                                  <label>
                                    {product.product} {product.eenheid && `(${product.eenheid})`}
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={newQuantities[product.ID] || ''}
                                    onChange={(e) =>
                                      handleNewQuantityChange(product.ID, e.target.value)
                                    }
                                    placeholder="0"
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="no-new-products-text">
                              Alle producten uit deze groep staan al in je bestelling.
                            </p>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <p className="producten-panel-empty">
                      Zoek een product of selecteer een productgroep om extra producten toe te voegen.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {newVrijeRegels.length > 0 && (
              <div className="form-section">
                <strong>Nieuwe vrije producten (Anders)</strong>
                <div className="producten-list">
                  {newVrijeRegels.map((regel, index) => (
                    <div key={`new-vrij-${index}`} className="product-item">
                      <label>{regel.omschrijving}</label>
                      <span className="vrije-regel-qty">{regel.hoeveelheid}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="edit-anders-button"
                  onClick={() => setShowAndersPage(true)}
                >
                  Wijzig nieuwe vrije producten
                </button>
              </div>
            )}
          </div>

          <div className="action-buttons">
            <button
              type="button"
              onClick={handleLeave}
              className="cancel-button"
              disabled={saving}
            >
              Annuleren
            </button>
            <button
              onClick={handleSave}
              className="save-button"
              disabled={saving}
            >
              {saving ? 'Opslaan...' : 'Wijzigingen opslaan'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default BestellingBewerkenPage;
